import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

function BlogNervousSystemSafety() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <Navigation variant="soft-brown" scrolled={true} />
      
      {/* Article Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-sage-600 hover:text-sage-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            Your Nervous System Is Either Creating Safety... Or Keeping You Stuck
          </h1>
          
          <div className="flex items-center text-gray-600 text-sm mb-8">
            <span>By Yasi Philipos</span>
            <span className="mx-3">•</span>
            <span>8 min read</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Most people-pleasers are exceptional at reading the room—you can feel the shift in energy before anyone else notices. But this hyper-awareness comes at a cost.
            </p>

            <div className="bg-gradient-to-r from-sage-50 to-warm-50 p-8 rounded-xl border border-sage-200 my-8">
              <h2 className="text-2xl font-serif font-light text-sage-800 mb-4">
                The Gift That Became a Burden
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Your ability to sense emotional shifts, anticipate needs, and adjust your behavior was once a survival strategy. It kept you safe. It earned you approval. It made you feel less alone.
              </p>
              <p className="text-gray-700 leading-relaxed">
                And it worked... until it didn't.
              </p>
            </div>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              When Safety Feels Like Risk
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">
              Boundaries can feel risky, as if they'll create disconnection. Making requests or staying steady when there's pushback can feel challenging. Not because you don't know what boundaries are, but because your nervous system has learned that boundaries = danger.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              The Problem
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">You find yourself in patterns that feel impossible to break:</p>

            <ul className="space-y-4 my-8">
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">You're over-explaining every boundary, but people still get upset.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">You feel guilty for having needs, so you abandon them instead.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">You're attracting people who expect you to sacrifice yourself... because that's what you've often done.</span>
              </li>
            </ul>

            <div className="bg-gradient-to-r from-warm-50 to-sage-50 border-l-4 border-warm-500 p-8 my-10 rounded-xl">
              <p className="text-lg font-medium text-gray-800 mb-3">
                Here's the truth:
              </p>
              <p className="text-gray-700 leading-relaxed">
                You don't need more boundary scripts. You don't need another workshop on saying "no." You need <strong className="text-warm-700">nervous system regulation</strong>.
              </p>
            </div>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              What Is Nervous System Regulation?
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">
              Nervous system regulation is the process of teaching your body that it's safe to have needs, safe to take up space, and safe to say no. It's about moving from a state of chronic hyper-vigilance (constantly scanning for threats, people's moods, potential conflicts) to a state where you can rest, connect authentically, and respond from choice rather than fear.
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              When your nervous system is dysregulated, your body believes you're in danger—even when you're not. This is why:
            </p>

            <ul className="space-y-3 my-8 ml-6">
              <li className="text-gray-700 leading-relaxed">Setting a boundary can trigger panic</li>
              <li className="text-gray-700 leading-relaxed">Saying "no" can feel like you're about to be abandoned</li>
              <li className="text-gray-700 leading-relaxed">Resting can feel unsafe (because you've learned that staying useful keeps you safe)</li>
              <li className="text-gray-700 leading-relaxed">Speaking up can flood your system with guilt and anxiety</li>
            </ul>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              The Path Forward
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">
              Healing people-pleasing patterns isn't about willpower or positive thinking. It's about helping your nervous system update its operating system. It's about creating new neural pathways that say:
            </p>

            <div className="bg-sage-50 p-8 rounded-xl my-8 border border-sage-200">
              <ul className="space-y-3">
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-sage-600">✓</span>
                  <span>It's safe to have boundaries</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-sage-600">✓</span>
                  <span>It's safe to be seen in your fullness</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-sage-600">✓</span>
                  <span>It's safe to take up space</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-sage-600">✓</span>
                  <span>It's safe to rest</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2">
                  <span className="text-sage-600">✓</span>
                  <span>It's safe to be authentic</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-700 mb-8 leading-relaxed">
              This work happens in the body, not just in the mind. Through somatic practices, co-regulation in safe relationships, and gentle nervous system rewiring, you can teach your body a new way of being.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              You're Not Broken
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Your people-pleasing patterns aren't a character flaw. They're evidence of your incredible adaptability—your nervous system's brilliant way of keeping you safe in environments where your authentic self wasn't fully welcome.
            </p>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              The good news? You can honor that survival strategy while gently creating space for something new. Something that allows you to show up as your full, authentic self—boundaries and all.
            </p>

            {/* Author Bio */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-sage-200 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Yasi Philipos</h3>
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                    Licensed Marriage and Family Therapist specializing in helping people-pleasers reclaim their authentic voice through nervous system regulation and somatic healing.
                  </p>
                  <Link to="/about" className="text-warm-600 hover:text-warm-700 text-sm font-medium">
                    Learn more about Yasi →
                  </Link>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-br from-sage-50 to-warm-50 rounded-2xl p-8 text-center border border-warm-200">
              <h3 className="text-2xl font-serif font-light text-sage-800 mb-4">
                Ready to Regulate Your Nervous System?
              </h3>
              <p className="text-gray-700 mb-6 max-w-xl mx-auto leading-relaxed">
                Join The Group Coaching Program—a somatic group program designed to help you move from self-abandonment to self-trust.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/services/group-coaching"
                  className="inline-block px-8 py-3 bg-warm-600 text-white rounded-lg font-medium hover:bg-warm-700 transition-colors"
                >
                  Learn About Group Coaching
                </Link>
                <Link
                  to="/services/individual-sessions"
                  className="inline-block px-8 py-3 bg-white text-sage-700 border-2 border-sage-300 rounded-lg font-medium hover:bg-sage-50 transition-colors"
                >
                  Explore Individual Sessions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BlogNervousSystemSafety;
